/**
 * 단계별 import 상태 관리 훅.
 *
 * 서버는 stateless — 매 호출마다 state 를 round-trip. 훅은 그 round-trip 결과를
 * stage 별 스냅샷으로 보관하고, 사용자의 HTML 편집을 별도 저장한다.
 *
 * 흐름:
 *  - selectStage(name): 뷰를 특정 stage 로 전환 (아무 실행 없음)
 *  - editHtml(name, html): 해당 stage 출력의 편집본 저장 (아직 서버로 가지 않음)
 *  - runNextStage(): 마지막 완료 stage 의 편집본을 입력으로 다음 stage 실행
 *  - rerunFromStage(name): 해당 stage 의 이전 stage 편집본을 입력으로 재실행
 *    (이후 stage 스냅샷·편집은 invalidated)
 *  - commit(): 모든 stage 완료 + validation 무오류 시 최종 저장
 */

import { useCallback, useMemo, useReducer, useRef } from 'react'
import { useToast } from '../../components/ToastContainer'
import {
  STAGE_ORDER,
  STAGE_META,
  emptyStepwiseState,
  prevStage,
  nextStage,
  type StageName,
  type StageStatus,
  type StepwiseState,
} from './stages'

// ─────────────────────────────────────────────────────
// Reducer state
// ─────────────────────────────────────────────────────

interface InternalState {
  /** stage 별로 해당 stage 가 "완료된 직후" 의 전체 StepwiseState 스냅샷. */
  snapshots: Partial<Record<StageName, StepwiseState>>
  /** stage 별 사용자 편집 HTML. 해당 stage 의 "출력" 을 사용자가 수정한 것. */
  edited: Partial<Record<StageName, string>>
  /** stage 별 상태. */
  status: Record<StageName, StageStatus>
  /** 현재 선택된 stage. */
  selected: StageName
  /** 현재 실행 중인 stage (없으면 null). */
  running: StageName | null
  /** 마지막 에러 메시지 (API 레벨). */
  error: string | null
  /** 실행 시 서버로 보낼 skipLlm. 세션 시작값 기본, UI 에서 변경 가능. */
  skipLlm: boolean
  /** 실행 시 서버로 보낼 llmModel (undefined 면 서버 기본). */
  llmModel?: string
}

type Action =
  | { type: 'select'; stage: StageName }
  | { type: 'edit'; stage: StageName; html: string }
  | { type: 'runStart'; stage: StageName }
  | { type: 'runSuccess'; stage: StageName; state: StepwiseState; invalidateAfter: boolean }
  | { type: 'runFailure'; stage: StageName; error: string; state?: StepwiseState }
  | { type: 'apiError'; error: string }
  | { type: 'setSkipLlm'; value: boolean }
  | { type: 'setLlmModel'; value?: string }
  | { type: 'reset' }

function initialState(
  initialServerState: StepwiseState,
  initialStage: StageName,
  skipLlm: boolean,
  llmModel?: string,
): InternalState {
  const status = Object.fromEntries(STAGE_ORDER.map((s) => [s, 'idle'])) as Record<StageName, StageStatus>
  // 이미 initialServerState.stages 에 성공으로 기록된 stage 들을 snapshot 으로 채움.
  const snapshots: Partial<Record<StageName, StepwiseState>> = {}
  if (initialServerState.stages.some((s) => s.ok)) {
    // 모든 성공 stage 에 동일한 (현재) 스냅샷을 심어두고, 이후 재실행으로 교체.
    for (const log of initialServerState.stages) {
      if (log.ok) {
        snapshots[log.stage] = initialServerState
        status[log.stage] = 'done'
      } else {
        status[log.stage] = 'error'
      }
    }
  }
  return {
    snapshots,
    edited: {},
    status,
    selected: initialStage,
    running: null,
    error: null,
    skipLlm,
    llmModel,
  }
}

function reduce(state: InternalState, action: Action): InternalState {
  switch (action.type) {
    case 'select':
      return { ...state, selected: action.stage }
    case 'edit':
      return { ...state, edited: { ...state.edited, [action.stage]: action.html } }
    case 'runStart':
      return { ...state, running: action.stage, error: null, status: { ...state.status, [action.stage]: 'running' } }
    case 'runSuccess': {
      const nextSnapshots = { ...state.snapshots, [action.stage]: action.state }
      const nextEdited = { ...state.edited }
      const nextStatus = { ...state.status, [action.stage]: 'done' as StageStatus }
      if (action.invalidateAfter) {
        const startIdx = STAGE_ORDER.indexOf(action.stage) + 1
        for (let i = startIdx; i < STAGE_ORDER.length; i++) {
          const s = STAGE_ORDER[i]
          delete nextSnapshots[s]
          delete nextEdited[s]
          nextStatus[s] = 'invalidated'
        }
      }
      return {
        ...state,
        snapshots: nextSnapshots,
        edited: nextEdited,
        status: nextStatus,
        running: null,
        selected: action.stage,
      }
    }
    case 'runFailure': {
      const nextSnapshots = action.state
        ? { ...state.snapshots, [action.stage]: action.state }
        : state.snapshots
      return {
        ...state,
        snapshots: nextSnapshots,
        status: { ...state.status, [action.stage]: 'error' },
        running: null,
        selected: action.stage,
        error: action.error,
      }
    }
    case 'apiError':
      return { ...state, running: null, error: action.error }
    case 'setSkipLlm':
      return { ...state, skipLlm: action.value }
    case 'setLlmModel':
      return { ...state, llmModel: action.value }
    case 'reset':
      return initialState(emptyStepwiseState(), 'extract', state.skipLlm, state.llmModel)
  }
}

// ─────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────

export interface StepwiseSession {
  sessionId: string
  /** 원본 ZIP. 재분석 모드에서 이력에 sourceZipS3Key 가 없으면 undefined. extract/upload 재실행 불가. */
  zipS3Key?: string
  name: string
  slug: string
  skipLlm: boolean
  llmModel?: string
  /** 재분석 모드 — 기존 템플릿 ID. 있으면 commit 이 UPDATE, 없으면 INSERT. */
  templateId?: string
  /** 초기 state (재분석 시 extract/upload 완료 상태로 pre-seed, 자동 모드 실패 시 중간 상태 등). */
  initialState?: StepwiseState
  /** 초기 선택 stage (보통 'extract' 또는 실패한 stage). */
  initialStage?: StageName
}

export interface UseStepwiseImportReturn {
  // 메타
  session: StepwiseSession
  // 현재 선택 stage
  selected: StageName
  selectStage: (stage: StageName) => void
  // stage 별 상태
  status: Record<StageName, StageStatus>
  running: StageName | null
  error: string | null
  // stage 별 HTML (편집본 우선, 없으면 snapshot 의 html)
  htmlAt: (stage: StageName) => string
  /** stage 별 스냅샷 (없으면 undefined — 아직 실행되지 않음). */
  snapshotAt: (stage: StageName) => StepwiseState | undefined
  /** 해당 stage 의 HTML 을 사용자가 편집한 값이 있는지. */
  isEdited: (stage: StageName) => boolean
  // 편집
  editHtml: (stage: StageName, html: string) => void
  // 실행 (선택적으로 systemPromptOverride 전달 — LLM 모달에서 편집한 system prompt)
  runNextStage: (opts?: { systemPromptOverride?: string }) => Promise<void>
  rerunFromStage: (stage: StageName, opts?: { systemPromptOverride?: string }) => Promise<void>
  // 완료
  canCommit: boolean
  commit: () => Promise<{ templateId: string; conversionId: string } | null>
  reset: () => void
  // 파생
  lastCompletedStage: StageName | null
  pendingNextStage: StageName | null
  // 실행 옵션 (UI 에서 변경 가능)
  skipLlm: boolean
  setSkipLlm: (value: boolean) => void
  llmModel?: string
  setLlmModel: (value?: string) => void
  // LLM 전용: 실행 전 payload 미리보기 + 중지
  previewLlmPayload: (target: StageName) => Promise<LlmPayloadPreview | null>
  cancelRunningStage: () => void
}

export interface LlmPayloadPreview {
  system: string
  userMessage: string
  model?: string
  maxTokens: number
  maskedScriptCount: number
  maskedStyleCount: number
  maskedHtmlLength: number
  originalHtmlLength: number
}

interface InitResponse {
  sessionId: string
  zipS3Key: string
  name: string
  slug: string
  skipLlm: boolean
  llmModel?: string
  state: StepwiseState
}

/**
 * init 요청 — 모달에서 사용. 훅 외부에서 호출 후 결과를 session 으로 훅에 전달한다.
 */
export async function initStepwiseImport(params: {
  file: File
  name: string
  slug: string
  skipLlm: boolean
  llmModel?: string
}): Promise<InitResponse> {
  const fd = new FormData()
  fd.set('file', params.file, params.file.name)
  fd.set('name', params.name.trim())
  fd.set('slug', params.slug.trim())
  fd.set('skipLlm', String(params.skipLlm))
  if (params.llmModel) fd.set('llmModel', params.llmModel)

  const res = await fetch('/api/admin/templates/import/step/init', {
    method: 'POST',
    body: fd,
    credentials: 'include',
  })
  const json = (await res.json()) as { success: boolean; data?: InitResponse; error?: { message?: string } }
  if (!json.success || !json.data) {
    throw new Error(json.error?.message ?? `init 실패 (${res.status})`)
  }
  return json.data
}

export function useStepwiseImport(session: StepwiseSession): UseStepwiseImportReturn {
  const { addToast } = useToast()
  const [s, dispatch] = useReducer(
    reduce,
    undefined,
    () => initialState(
      session.initialState ?? emptyStepwiseState(),
      session.initialStage ?? 'extract',
      session.skipLlm,
      session.llmModel,
    )
  )

  // 동시 실행 방지 락.
  const lock = useRef(false)
  // 실행 중인 요청의 AbortController — '중지' 버튼용.
  const abortRef = useRef<AbortController | null>(null)

  const selectStage = useCallback((stage: StageName) => dispatch({ type: 'select', stage }), [])
  const editHtml = useCallback((stage: StageName, html: string) => dispatch({ type: 'edit', stage, html }), [])
  const reset = useCallback(() => dispatch({ type: 'reset' }), [])

  const htmlAt = useCallback(
    (stage: StageName): string => {
      if (s.edited[stage] !== undefined) return s.edited[stage]!
      return s.snapshots[stage]?.html ?? ''
    },
    [s.edited, s.snapshots]
  )

  const snapshotAt = useCallback((stage: StageName) => s.snapshots[stage], [s.snapshots])
  const isEdited = useCallback((stage: StageName) => s.edited[stage] !== undefined, [s.edited])

  // 가장 마지막에 성공(done) 한 stage.
  const lastCompletedStage = useMemo<StageName | null>(() => {
    for (let i = STAGE_ORDER.length - 1; i >= 0; i--) {
      if (s.status[STAGE_ORDER[i]] === 'done') return STAGE_ORDER[i]
    }
    return null
  }, [s.status])

  const pendingNextStage = useMemo<StageName | null>(() => {
    return lastCompletedStage ? nextStage(lastCompletedStage) : STAGE_ORDER[0]
  }, [lastCompletedStage])

  const canCommit = useMemo(() => {
    if (s.status.inject !== 'done') return false
    const snap = s.snapshots.inject
    if (!snap) return false
    if (snap.validation.errors.length > 0) return false
    // 모든 stage 가 done 인지 확인.
    return STAGE_ORDER.every((st) => s.status[st] === 'done')
  }, [s.status, s.snapshots])

  /**
   * 공통 실행 로직 — target stage 를 서버에 요청.
   * `baseStage` 의 snapshot/편집을 입력 state 로 사용.
   */
  const runStageInternal = useCallback(
    async (target: StageName, opts: { invalidateAfter: boolean; systemPromptOverride?: string }) => {
      if (lock.current) return
      lock.current = true
      try {
        dispatch({ type: 'runStart', stage: target })

        // 입력 state 선정.
        const base = prevStage(target)
        const baseSnapshot = base ? s.snapshots[base] : undefined
        const inputState = baseSnapshot ?? emptyStepwiseState()
        const modifiedHtml = base ? s.edited[base] : undefined

        const body = {
          sessionId: session.sessionId,
          zipS3Key: session.zipS3Key,
          slug: session.slug,
          targetStage: target,
          state: inputState,
          modifiedHtml,
          skipLlm: s.skipLlm,
          /**
           * 단계별 UI 에서 'LLM 실행' 을 선택했으면 force=true 로 보내
           * `hasHardcodedCandidates` no-candidates 조기 스킵을 우회한다 (명시적 실행 의도).
           */
          forceLlm: !s.skipLlm,
          llmModel: s.llmModel,
          systemPromptOverride: opts.systemPromptOverride,
        } as const
        const controller = new AbortController()
        abortRef.current = controller
        const res = await fetch('/api/admin/templates/import/step/run', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
          signal: controller.signal,
        })
        // 초기 검증 실패는 JSON 응답 (스트림 진입 전 early return).
        const contentType = res.headers.get('content-type') ?? ''
        if (!contentType.includes('text/event-stream')) {
          const err = (await res.json().catch(() => ({}))) as { error?: { message?: string } }
          const msg = err.error?.message ?? `stage '${STAGE_META[target].label}' 실행 실패 (${res.status})`
          dispatch({ type: 'apiError', error: msg })
          addToast({ type: 'error', message: msg })
          return
        }

        // SSE 스트림 파서 — `data: {...}` 라인 단위로 done/error/keepalive 이벤트 분기.
        const reader = res.body?.getReader()
        if (!reader) {
          dispatch({ type: 'apiError', error: 'SSE 스트림 없음' })
          return
        }
        const decoder = new TextDecoder()
        let buf = ''
        let finalState: StepwiseState | null = null
        let finalResult: { ok: boolean; fatal: boolean } | null = null
        let finalError: string | null = null
        outer: while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const lines = buf.split('\n')
          buf = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const ev = JSON.parse(line.slice(6)) as
                | { type: 'keepalive' }
                | { type: 'done'; state: StepwiseState; result: { ok: boolean; fatal: boolean } }
                | { type: 'error'; error: string }
              if (ev.type === 'keepalive') continue
              if (ev.type === 'done') {
                finalState = ev.state
                finalResult = ev.result
                break outer
              }
              if (ev.type === 'error') {
                finalError = ev.error
                break outer
              }
            } catch {
              /* noop — 파싱 실패한 라인은 무시 */
            }
          }
        }

        if (finalError !== null) {
          dispatch({ type: 'apiError', error: finalError })
          addToast({ type: 'error', message: finalError })
          return
        }
        if (!finalState || !finalResult) {
          const msg = `stage '${STAGE_META[target].label}' 응답이 완료 이벤트 없이 종료됨`
          dispatch({ type: 'apiError', error: msg })
          addToast({ type: 'error', message: msg })
          return
        }
        if (finalResult.ok) {
          dispatch({ type: 'runSuccess', stage: target, state: finalState, invalidateAfter: opts.invalidateAfter })
        } else {
          const firstErr = finalState.errors[finalState.errors.length - 1]
          const msg = firstErr ? `[${firstErr.stage}${firstErr.code ? '/' + firstErr.code : ''}] ${firstErr.message}` : `stage '${STAGE_META[target].label}' 실패`
          dispatch({ type: 'runFailure', stage: target, error: msg, state: finalState })
          addToast({ type: 'error', message: msg })
        }
      } catch (err) {
        const isAbort = err instanceof DOMException && err.name === 'AbortError'
        const msg = isAbort ? '사용자가 중지했습니다' : err instanceof Error ? err.message : String(err)
        dispatch({ type: isAbort ? 'runFailure' : 'apiError', error: msg, stage: target } as Action)
        addToast({ type: isAbort ? 'info' : 'error', message: msg })
      } finally {
        lock.current = false
        abortRef.current = null
      }
    },
    [s.snapshots, s.edited, s.skipLlm, s.llmModel, session, addToast]
  )

  const cancelRunningStage = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const previewLlmPayload = useCallback(
    async (target: StageName): Promise<LlmPayloadPreview | null> => {
      const base = prevStage(target)
      const baseSnapshot = base ? s.snapshots[base] : undefined
      const inputState = baseSnapshot ?? emptyStepwiseState()
      const modifiedHtml = base ? s.edited[base] : undefined
      try {
        const res = await fetch('/api/admin/templates/import/step/llm-preview', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            state: inputState,
            modifiedHtml,
            llmModel: s.llmModel,
          }),
        })
        const json = (await res.json()) as {
          success: boolean
          data?: LlmPayloadPreview
          error?: { message?: string }
        }
        if (!json.success || !json.data) {
          const msg = json.error?.message ?? `LLM 미리보기 실패 (${res.status})`
          addToast({ type: 'error', message: msg })
          return null
        }
        return json.data
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        addToast({ type: 'error', message: msg })
        return null
      }
    },
    [s.snapshots, s.edited, s.llmModel, addToast]
  )

  const runNextStage = useCallback(
    async (opts?: { systemPromptOverride?: string }) => {
      if (!pendingNextStage) return
      await runStageInternal(pendingNextStage, { invalidateAfter: false, systemPromptOverride: opts?.systemPromptOverride })
    },
    [pendingNextStage, runStageInternal]
  )

  const rerunFromStage = useCallback(
    async (stage: StageName, opts?: { systemPromptOverride?: string }) => {
      await runStageInternal(stage, { invalidateAfter: true, systemPromptOverride: opts?.systemPromptOverride })
    },
    [runStageInternal]
  )

  const commit = useCallback(async () => {
    if (!canCommit) return null
    const snap = s.snapshots.inject!
    const finalHtml = s.edited.inject ?? snap.html
    const stateToSend: StepwiseState = s.edited.inject !== undefined ? { ...snap, html: finalHtml } : snap

    try {
      const res = await fetch('/api/admin/templates/import/step/commit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          templateId: session.templateId,
          zipS3Key: session.zipS3Key,
          name: session.name,
          slug: session.slug,
          state: stateToSend,
        }),
      })
      const json = (await res.json()) as {
        success: boolean
        data?: { templateId: string; conversionId: string }
        error?: { message?: string }
      }
      if (!json.success || !json.data) {
        const msg = json.error?.message ?? `commit 실패 (${res.status})`
        dispatch({ type: 'apiError', error: msg })
        addToast({ type: 'error', message: msg })
        return null
      }
      addToast({ type: 'success', message: '템플릿 저장 완료' })
      return json.data
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      dispatch({ type: 'apiError', error: msg })
      addToast({ type: 'error', message: msg })
      return null
    }
  }, [canCommit, s.snapshots, s.edited, session, addToast])

  const setSkipLlm = useCallback((value: boolean) => dispatch({ type: 'setSkipLlm', value }), [])
  const setLlmModel = useCallback((value?: string) => dispatch({ type: 'setLlmModel', value }), [])

  return {
    session,
    selected: s.selected,
    selectStage,
    status: s.status,
    running: s.running,
    error: s.error,
    htmlAt,
    snapshotAt,
    isEdited,
    editHtml,
    runNextStage,
    rerunFromStage,
    canCommit,
    commit,
    reset,
    lastCompletedStage,
    pendingNextStage,
    skipLlm: s.skipLlm,
    setSkipLlm,
    llmModel: s.llmModel,
    setLlmModel,
    previewLlmPayload,
    cancelRunningStage,
  }
}
